import { updateTeamSetting } from '../../reducers/user';
import changedColor from '../assets/changedColor';
import { getSearchProps, setSearchRoute } from '../../../helpers/history';
import { COLOR_LABELS } from '../../../helpers/colorsHelper';

/**
 * Applies a configuration change and, when committing, drops filters / asset colors
 * that point to removed labels. Optimistic delete must skip those side effects:
 * a failed request rolls the label list back, and clearing filters/assets first would
 * leave search and catalog out of sync with the restored configuration.
 */
const applyColorLabelsUpdate = (
  labels,
  initiator,
  { clearStaleAssets = true } = {},
) => (dispatch, getState) => {
  dispatch(updateTeamSetting({
    key: 'colorLabels',
    value: labels,
  }));

  // Keep search filters and asset colors until the write is confirmed (or a socket
  // update commits the new config). Rollback after a failed delete otherwise cannot
  // put the previous color filter back into the route.
  if (!clearStaleAssets) return;

  const availableSlugs = new Set([
    COLOR_LABELS.noColor,
    ...labels.map(({ slug }) => slug),
  ]);

  // The server resets the color on these assets, but the request carries no asset list:
  // without this the slug would linger in the store and light up again if the same slot
  // is reused for a new label before the catalog is refetched.
  const staleAssetIds = (getState().assets?.items || [])
    .filter(({ color }) => color && !availableSlugs.has(color))
    .map(({ _id }) => _id);

  if (staleAssetIds.length) {
    dispatch(changedColor(
      staleAssetIds,
      COLOR_LABELS.noColor,
      new Date().toISOString(),
      initiator || {},
    ));
  }

  const searchProps = getSearchProps();
  if (!Array.isArray(searchProps.color)) return;

  const colors = searchProps.color.filter((slug) => availableSlugs.has(slug));
  if (colors.length === searchProps.color.length) return;

  if (colors.length) {
    searchProps.color = colors;
  } else {
    delete searchProps.color;
  }
  setSearchRoute(searchProps, { shouldReplace: true });
};

export default applyColorLabelsUpdate;
